import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSettings } from "@/lib/settings";

/**
 * Manages document.title, description, keywords, OG/Twitter tags, canonical link,
 * favicon, apple-touch-icon and JSON-LD structured data per route.
 */
const ROUTE_META = {
  "/":       { title: null, desc: null },
  "/about":  { title: "About Us", desc: "Learn about EQtech Academy — AI-based skill academy for career-oriented learning." },
  "/courses":{ title: "Courses", desc: "Explore AI-powered courses at EQtech Academy — the AI skill development marketplace." },
  "/services":{ title: "Services", desc: "Corporate training, internships, placement and mentorship from EQtech Academy." },
  "/partner-with-us":{ title: "Partner With Us", desc: "Partner with EQtech Academy — a skill development marketplace. Sell your course, reach students directly and grow with our AI career academy." },
  "/blog":   { title: "Blog", desc: "AI marketing insights, tutorials and career tips from our experts." },
  "/gallery":{ title: "Gallery", desc: "Life at EQtech Academy — students, workshops and events." },
  "/contact":{ title: "Contact Us", desc: "Get in touch with EQtech Academy — Kochi, Kerala." },
  "/trainers":{ title: "Our Trainers", desc: "Meet the industry experts guiding your journey." },
  "/certification":{ title: "Certification", desc: "Industry-recognized certifications from EQtech Academy." },
  "/careers":{ title: "Careers", desc: "Join our team — open roles at EQtech Academy." },
  "/corporate-training":{ title: "Corporate Training", desc: "Custom AI marketing workshops for your team." },
  "/internship":{ title: "Internships", desc: "Live client project internships with certification." },
  "/placement":{ title: "Placement Assistance", desc: "100% placement support with 50+ hiring partners." },
  "/refund-policy":{ title: "Refund Policy" },
  "/privacy":{ title: "Privacy Policy" },
  "/terms":  { title: "Terms & Conditions" },
};

function upsertMeta({ name, property, content }) {
  if (!content) return;
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    if (name) el.setAttribute("name", name);
    if (property) el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink({ rel, href, id }) {
  if (!href) return;
  const selector = id ? `link[id="${id}"]` : `link[rel="${rel}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (id) el.setAttribute("id", id);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function SEOManager() {
  const { settings } = useSettings();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!settings) return;
    const siteName = settings?.site_name || "EQtech Academy";
    const defaultTitle = settings?.seo?.meta_title || siteName;
    const defaultDesc = settings?.seo?.meta_description || "";
    const route = ROUTE_META[pathname] || {};
    // Partner page: prefer admin-managed SEO fields when present
    const partnerSeo = (pathname === "/partner-with-us") ? (settings?.partner_page?.seo || {}) : {};
    const title = partnerSeo.meta_title || (route.title ? `${route.title} | ${siteName}` : defaultTitle);
    document.title = title;

    const desc = partnerSeo.meta_description || route.desc || defaultDesc;
    const keywords = partnerSeo.keywords || settings?.seo?.keywords || "";
    const ogImage = settings?.seo?.og_image || settings?.branding?.logo_light || "";
    const twitter = settings?.seo?.twitter_handle || "";
    const url = typeof window !== "undefined" ? window.location.origin + pathname : "";

    upsertMeta({ name: "description", content: desc });
    upsertMeta({ name: "keywords", content: keywords });
    upsertMeta({ name: "robots", content: settings?.seo?.noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large" });
    upsertMeta({ name: "author", content: siteName });
    upsertMeta({ name: "theme-color", content: "#0ea5e9" });

    // Open Graph
    upsertMeta({ property: "og:type", content: pathname === "/" ? "website" : "article" });
    upsertMeta({ property: "og:site_name", content: siteName });
    upsertMeta({ property: "og:title", content: title });
    upsertMeta({ property: "og:description", content: desc });
    upsertMeta({ property: "og:url", content: url });
    upsertMeta({ property: "og:image", content: ogImage });
    upsertMeta({ property: "og:locale", content: "en_IN" });

    // Twitter
    upsertMeta({ name: "twitter:card", content: ogImage ? "summary_large_image" : "summary" });
    upsertMeta({ name: "twitter:title", content: title });
    upsertMeta({ name: "twitter:description", content: desc });
    upsertMeta({ name: "twitter:image", content: ogImage });
    if (twitter) upsertMeta({ name: "twitter:site", content: twitter });

    // Canonical
    upsertLink({ rel: "canonical", href: url, id: "canonical-link" });

    // Favicon
    const fav = settings?.branding?.favicon;
    if (fav) upsertLink({ rel: "icon", href: fav });
    const apple = settings?.branding?.apple_touch_icon;
    if (apple) upsertLink({ rel: "apple-touch-icon", href: apple });

    // JSON-LD structured data
    if (settings?.seo?.structured_data_enabled !== false) {
      const origin = typeof window !== "undefined" ? window.location.origin : "";
      const social = settings?.social || {};
      const contact = settings?.contact || {};
      const sameAs = [social.facebook, social.instagram, social.linkedin, social.youtube, social.twitter].filter(Boolean);
      upsertJsonLd("ld-org", {
        "@context": "https://schema.org",
        "@type": "EducationalOrganization",
        "@id": `${origin}#organization`,
        name: siteName,
        alternateName: "EQtech Academy — AI Career Academy",
        url: origin,
        logo: settings?.branding?.logo_light || ogImage,
        description: defaultDesc,
        sameAs,
        address: contact.address ? {
          "@type": "PostalAddress",
          streetAddress: contact.address,
          addressCountry: "IN"
        } : undefined,
        contactPoint: (contact.phone || contact.email) ? [{
          "@type": "ContactPoint",
          contactType: "Admissions",
          telephone: contact.phone,
          email: contact.email,
          areaServed: "IN",
          availableLanguage: ["English", "Malayalam", "Hindi"]
        }] : undefined,
        knowsAbout: [
          "AI-based skill academy",
          "AI career academy",
          "career-oriented learning",
          "AI skill development marketplace",
          "skill development marketplace",
          "AI marketing",
          "internships",
          "placement assistance"
        ]
      });
      upsertJsonLd("ld-website", {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${origin}#website`,
        url: origin,
        name: siteName,
        potentialAction: {
          "@type": "SearchAction",
          target: `${origin}/courses?q={search_term_string}`,
          "query-input": "required name=search_term_string"
        }
      });
    } else {
      upsertJsonLd("ld-org", null);
      upsertJsonLd("ld-website", null);
    }
  }, [pathname, settings]);

  return null;
}
